import { useRef, useState } from "react";

import { type ParsedPantryView, api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { pantryCopy } from "../lib/i18n/pantry";
import { fileToCompressedDataUrl } from "../lib/image";
import { ScreenShell } from "./screen-shell";
import { IconCamera, IconKeyboard } from "./ui/icon";
import { H1, TopBar } from "./ui/primitives";

type Props = {
  householdId: string;
  onParsed: (parsed: ParsedPantryView) => void;
  onCancel: () => void;
};

export function PantryPhotoScreen({ householdId, onParsed, onCancel }: Props) {
  const { lang } = useLang();
  const t = pantryCopy[lang];
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File | null) {
    if (!file) return;
    setError(null);
    setSubmitting(true);
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      setPreview(dataUrl);
      const parsed = await api.parsePantryPhoto(householdId, dataUrl);
      if (parsed.items.length === 0) {
        setError(t.photoNoItems);
        return;
      }
      onParsed(parsed);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.photoErrAnalyse);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ScreenShell hideHeader>
      <TopBar back={t.photoBack} title={t.photoTopTitle} onBack={onCancel} />

      <div style={{ paddingTop: 18, paddingBottom: 6 }}>
        <H1 accent=".">{t.photoTitle}</H1>
        <p style={{ marginTop: 10, fontSize: 14, color: "var(--ink-2)" }}>{t.photoIntro}</p>
      </div>

      {/* viewfinder */}
      <div
        style={{
          marginTop: 18,
          height: 340,
          borderRadius: 18,
          position: "relative",
          background: "var(--ink)",
          overflow: "hidden",
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(255,255,255,0.03) 0 1px, transparent 1px 12px)",
        }}
      >
        {preview ? (
          <img
            src={preview}
            alt={t.photoPreviewAlt}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : null}

        {/* corner brackets */}
        {([0, 1, 2, 3] as const).map((i) => {
          const isTop = i < 2;
          const isLeft = i === 0 || i === 2;
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                top: isTop ? 20 : undefined,
                bottom: !isTop ? 20 : undefined,
                left: isLeft ? 20 : undefined,
                right: !isLeft ? 20 : undefined,
                width: 28,
                height: 28,
                borderTop: isTop ? "2px solid var(--accent)" : "none",
                borderBottom: !isTop ? "2px solid var(--accent)" : "none",
                borderLeft: isLeft ? "2px solid var(--accent)" : "none",
                borderRight: !isLeft ? "2px solid var(--accent)" : "none",
              }}
            />
          );
        })}

        <div
          style={{
            position: "absolute",
            top: 20,
            left: 20,
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            color: "var(--accent)",
            letterSpacing: "0.1em",
            fontWeight: 700,
          }}
        >
          {submitting ? t.photoStatusAnalysing : t.photoStatusRec}
        </div>

        {!preview ? (
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%,-50%)",
              fontFamily: "var(--font-display)",
              fontSize: 60,
              fontWeight: 800,
              color: "rgba(255,255,255,0.18)",
              letterSpacing: "-0.05em",
            }}
            aria-hidden="true"
          >
            ❄
          </div>
        ) : null}

        <div
          style={{
            position: "absolute",
            bottom: 20,
            left: 20,
            right: 20,
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            color: "rgba(255,255,255,0.55)",
            letterSpacing: "0.06em",
          }}
        >
          {t.photoViewfinderHint}
        </div>
      </div>

      {/* tips */}
      <div className="flex gap-2" style={{ marginTop: 14 }}>
        {[t.photoTipLight, t.photoTipFromAbove, t.photoTipWholeFront].map((tip) => (
          <span
            key={tip}
            className="flex-1 text-center"
            style={{
              padding: "10px 8px",
              borderRadius: 10,
              fontSize: 11,
              fontWeight: 600,
              background: "var(--surface)",
              color: "var(--ink-2)",
              boxShadow: "0 0 0 1px var(--nav-border)",
            }}
          >
            {tip}
          </span>
        ))}
      </div>

      {error ? (
        <p
          className="text-[12px] px-3 py-2 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)", marginTop: 14 }}
        >
          {error}
        </p>
      ) : null}

      {/* native file inputs (camera + gallery) — preserved exactly */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          handleFile(file);
          e.target.value = "";
        }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          handleFile(file);
          e.target.value = "";
        }}
      />

      {/* actions row: keyboard fallback · big shutter · gallery */}
      <div
        className="flex items-center justify-center gap-3"
        style={{ marginTop: 22, marginBottom: 4 }}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="flex items-center justify-center transition-colors disabled:opacity-50"
          style={{
            width: 54,
            height: 54,
            borderRadius: 99,
            background: "var(--surface)",
            color: "var(--ink)",
            boxShadow: "0 0 0 1px var(--nav-border)",
          }}
          aria-label={t.photoAriaManual}
        >
          <IconKeyboard size={22} />
        </button>

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={submitting}
          className="flex items-center justify-center transition-all disabled:opacity-60"
          style={{
            width: 76,
            height: 76,
            borderRadius: 99,
            background: "var(--accent)",
            color: "var(--accent-ink)",
            boxShadow: "0 0 0 6px rgba(255,90,31,0.15), var(--shadow-cta)",
          }}
          aria-label={preview ? t.photoAriaRetake : t.photoAriaShoot}
        >
          <IconCamera size={28} />
        </button>

        <button
          type="button"
          onClick={() => galleryInputRef.current?.click()}
          disabled={submitting}
          className="flex items-center justify-center transition-colors disabled:opacity-50"
          style={{
            width: 54,
            height: 54,
            borderRadius: 99,
            background: "var(--surface)",
            color: "var(--ink)",
            boxShadow: "0 0 0 1px var(--nav-border)",
            fontSize: 18,
          }}
          aria-label={t.photoAriaGallery}
        >
          🖼
        </button>
      </div>

      <p
        className="text-center"
        style={{
          marginTop: 12,
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--muted)",
          letterSpacing: "0.06em",
        }}
      >
        {submitting
          ? t.photoCaptionAnalysing
          : preview
            ? t.photoCaptionRetake
            : t.photoCaptionShoot}
      </p>
    </ScreenShell>
  );
}
